/**
 * BoardV3 Main Component
 * Orchestrates layout, DnD, zoom, and renders board layers.
 * Child components are self-contained: sidebar, header, tasks layer, column backgrounds
 * read their state from boardLayoutStore via hooks.
 *
 * The boardLayoutStore is created and provided by BoardV3Page.
 * This component reads it from context and syncs layout values into it.
 */

import React, { memo, useRef, useCallback, useState, useMemo, useLayoutEffect, useEffect } from 'react';
import { useSelector } from '@xstate/store/react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { TaskPoolModalProvider } from './taskpool/TaskPoolModal';

// Components
import { BoardV3GridBackground } from './BoardV3GridBackground';
import { BoardV3TasksLayer } from './BoardV3TasksLayer';
import { BoardV3TaskBoxOverlay } from './taskbox/BoardV3TaskBoxOverlay';
import { BoardV3SnapIndicator } from './BoardV3SnapIndicator';
import { BoardV3DragInfoOverlay } from './BoardV3DragInfoOverlay';
import { BoardV3TimelineHeader } from './header/BoardV3TimelineHeader';
import { BoardV3Sidebar } from './BoardV3Sidebar';
import { BoardV3SelectionOverlay } from './BoardV3SelectionOverlay';
import { BoardV3ColumnBackgrounds } from './BoardV3ColumnBackgrounds';
import { selectionStore } from '../stores/selectionStore';
import { popupStore } from '../stores/popupStore';
import { BoardContextMenu } from './menus/BoardContextMenu';
import { ColumnContextMenu } from './menus/ColumnContextMenu';
import { useOpenTaskPoolModal } from './taskpool/TaskPoolModalContext';

// Hooks
import { useBoardTaskLayout } from '../hooks/useBoardTaskLayout';
import { useBoardPositionHelpers } from '../hooks/useBoardPositionHelpers';
import { useBoardZoomToPoint } from '../hooks/useBoardZoomToPoint';
import { useBoardHeaderZoomDrag } from '../hooks/useBoardHeaderZoomDrag';
import { useBoardDragHandlers } from '../hooks/useBoardDragHandlers';
import { useBoardSpacePan } from '../hooks/useBoardSpacePan';
import { useBoardBoxSelect } from '../hooks/useBoardBoxSelect';
import {
  useAllTasks,
  useBoardStartDate,
  useColumnNames,
  useRoadmapActions,
  useScheduledTasks,
} from '../../stores/hooks';
import { scheduleAfter } from '../stores/scheduleUtils';
import { recalculateSchedule } from '../stores/roadmapStore';

// Layout store (from context)
import { useBoardLayoutStore, useTaskDetailControls } from './BoardLayoutContext';

// Detail level constants
import { ROW_HEIGHTS } from '../hooks/useBoardTaskDetailLevel';

// Helpers & types
import { BASE_DAY_WIDTH } from '../utils/boardHelpers';
import { businessDayIndexToDate, getTaskDayIndex } from '../../lib/dateUtils';

// ============================================
// Types
// ============================================

export interface BoardV3Props {
  zoomLevel?: number;
  onZoomChange?: (zoomLevel: number) => void;
  className?: string;
}

// ============================================
// Main Component
// ============================================

export const BoardV3 = memo(function BoardV3({
  zoomLevel = 1,
  onZoomChange,
  className,
}: BoardV3Props) {
  // Store data via hooks
  const tasks = useAllTasks();
  const boardStartDate = useBoardStartDate();
  const columns = useColumnNames();
  const scheduledTasks = useScheduledTasks();
  const { moveTask, addToBoard } = useRoadmapActions();

  // Layout store from context (created by BoardV3Page)
  const layoutStore = useBoardLayoutStore();

  // Task detail from context
  const { detailLevel, isResizing: isDetailResizingCtx } = useTaskDetailControls();

  // Action callbacks with schedule recalculation
  const handleTaskMove = useCallback(
    (taskId: string, column: string, startDate: string) => {
      scheduleAfter(() => moveTask(taskId, column, startDate));
    },
    [moveTask]
  );

  const handleBulkMove = useCallback(
    (taskIds: string[], dayDelta: number, primaryTaskId: string, primaryColumn: string) => {
      for (const taskId of taskIds) {
        const task = tasks.find(t => t.id === taskId);
        if (!task) continue;
        const currentDay = getTaskDayIndex(task.startDate, boardStartDate);
        const newDay = Math.max(0, currentDay + dayDelta);
        const newDate = businessDayIndexToDate(newDay, boardStartDate);
        const newColumn = taskId === primaryTaskId ? primaryColumn : task.column;
        moveTask(taskId, newColumn, newDate);
      }
      setTimeout(() => recalculateSchedule(), 0);
    },
    [tasks, boardStartDate, moveTask]
  );

  const handleAddToBoard = useCallback(
    (taskId: string, column: string) => {
      scheduleAfter(() => addToBoard(taskId, column));
    },
    [addToBoard]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Grid config
  const dayWidth = BASE_DAY_WIDTH * zoomLevel;

  // Use detail-level-derived rowHeight for layout
  const currentRowHeight = ROW_HEIGHTS[detailLevel];

  // Read sidebar width from store for layout computation
  const liveSidebarWidth = useSelector(layoutStore, s => s.context.sidebarWidth);

  // Layout
  const layout = useBoardTaskLayout(tasks, columns, scheduledTasks, scrollPos, dayWidth, currentRowHeight, liveSidebarWidth, boardStartDate);

  // Sync layout values to store
  useLayoutEffect(() => {
    layoutStore.send({
      type: 'layout.update',
      dayWidth,
      columnYOffsets: layout.columnYOffsets,
      rowAssignments: layout.rowAssignments,
      tasksByColumn: layout.tasksByColumn,
      totalHeight: layout.totalHeight,
      totalDays: layout.totalDays,
      visibleWeeks: layout.visibleWeeks,
    });
  }, [layoutStore, dayWidth, layout.columnYOffsets, layout.rowAssignments, layout.tasksByColumn, layout.totalHeight, layout.totalDays, layout.visibleWeeks]);

  // Sync detail level from context (managed by useTaskDetailLevel in BoardV3Page)
  useLayoutEffect(() => {
    layoutStore.send({
      type: 'detail.update',
      detailLevel,
      rowHeight: ROW_HEIGHTS[detailLevel],
      isDetailResizing: !!isDetailResizingCtx,
    });
  }, [layoutStore, detailLevel, isDetailResizingCtx]);

  // Read live values from store (updated by child hooks: useSidebarResize, useHeaderResize)
  const liveColumnLabelWidth = useSelector(layoutStore, s => s.context.sidebarWidth);
  const liveRowHeight = useSelector(layoutStore, s => s.context.rowHeight);
  const liveHeaderHeight = useSelector(layoutStore, s => s.context.headerHeight);

  const { getColumnFromY, getDayFromX } = useBoardPositionHelpers(
    columns, layout.columnYOffsets, layout.rowAssignments,
    liveRowHeight, liveHeaderHeight, liveColumnLabelWidth, dayWidth,
  );

  // Zoom
  useBoardZoomToPoint(containerRef, zoomLevel, liveColumnLabelWidth, onZoomChange);
  const zoomDrag = useBoardHeaderZoomDrag(containerRef, zoomLevel, onZoomChange);

  // DnD
  const { dragState, handleDragStart, handleDragMove, handleDragEnd } = useBoardDragHandlers({
    columnYOffsets: layout.columnYOffsets,
    rowAssignments: layout.rowAssignments,
    columnLabelWidth: liveColumnLabelWidth,
    dayWidth,
    headerHeight: liveHeaderHeight,
    rowHeight: liveRowHeight,
    boardStartDate,
    getColumnFromY,
    getDayFromX,
    onTaskMove: handleTaskMove,
    tasks,
    onBulkMove: handleBulkMove,
  });

  // Space-bar pan
  const isSidebarResizing = useSelector(layoutStore, s => s.context.isSidebarResizing);
  const isHeaderResizing = useSelector(layoutStore, s => s.context.isHeaderResizing);
  const isDetailResizing = useSelector(layoutStore, s => s.context.isDetailResizing);

  useBoardSpacePan({
    containerRef,
    isDndDragging: dragState.activeTask !== null,
    isZoomDragging: zoomDrag.isZoomDragging,
    isSidebarResizing,
    isHeaderResizing,
    isDetailResizing,
  });

  // Task positions for box selection
  const taskPositions = useMemo(() => {
    const positions = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const column of columns) {
      for (const task of layout.tasksByColumn[column] || []) {
        const row = layout.rowAssignments[column]?.get(task.id) ?? 0;
        const duration = scheduledTasks.get(task.id)?.effectiveDays ?? 10;
        positions.set(task.id, {
          x: getTaskDayIndex(task.startDate, boardStartDate) * dayWidth,
          y: liveHeaderHeight + (layout.columnYOffsets[column] ?? 0) + row * liveRowHeight,
          width: duration * dayWidth,
          height: liveRowHeight,
        });
      }
    }
    return positions;
  }, [columns, layout.tasksByColumn, layout.rowAssignments, layout.columnYOffsets, boardStartDate, dayWidth, liveHeaderHeight, liveRowHeight, scheduledTasks]);

  // Box selection
  const boxSelect = useBoardBoxSelect({
    containerRef,
    contentRef,
    taskPositions,
    isDndDragging: dragState.activeTask !== null,
    isResizing: !!isDetailResizing,
  });

  // Scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setScrollPos({ x: target.scrollLeft, y: target.scrollTop });
  }, []);

  // Background click (deselect) — skip if rubber-band just ended
  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-task-id]')) return;
    if (selectionStore.getSnapshot().context.isBoxSelecting) return;
    selectionStore.send({ type: 'selection.clear' });
  }, []);

  // Board context menu
  const handleBoardContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const boardX = e.clientX - rect.left + container.scrollLeft - liveColumnLabelWidth;
    const boardY = e.clientY - rect.top + container.scrollTop;
    const day = Math.max(0, Math.floor(boardX / dayWidth));
    const column = getColumnFromY(boardY);
    if (!column) return;
    const date = businessDayIndexToDate(day, boardStartDate);
    popupStore.send({ type: 'popup.openBoardMenu', date, column, x: e.clientX, y: e.clientY });
  }, [liveColumnLabelWidth, dayWidth, getColumnFromY, boardStartDate]);

  const contentWidth = layout.totalDays * dayWidth;

  return (
    <TaskPoolModalProvider
      onAddToBoard={handleAddToBoard}
    >
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={containerRef}
          className={className}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'auto',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backgroundColor: '#f8fafc',
          }}
          onScroll={handleScroll}
        >
          <div
            style={{
              display: 'flex',
              minWidth: liveColumnLabelWidth + contentWidth,
              minHeight: liveHeaderHeight + layout.totalHeight,
            }}
          >
            <BoardV3Sidebar />

            {/* Main Content Area */}
            <div
              ref={contentRef}
              style={{
                position: 'relative',
                width: contentWidth,
                height: liveHeaderHeight + layout.totalHeight,
              }}
              onClick={handleBackgroundClick}
              onContextMenu={handleBoardContextMenu}
            >
              <BoardV3GridBackground />

              <BoardV3ColumnBackgrounds />

              <BoardV3TimelineHeader
                onZoomDragStart={onZoomChange ? zoomDrag.handleZoomDragStart : undefined}
                isZoomDragging={zoomDrag.isZoomDragging}
              />

              <BoardV3TasksLayer
                draggingTaskId={dragState.activeTask?.id ?? null}
              />

              {/* Snap Target Indicator + Drag Info */}
              {dragState.snapTarget && dragState.activeTask && (
                <>
                  <BoardV3SnapIndicator
                    column={dragState.snapTarget.column}
                    day={dragState.snapTarget.day}
                    duration={scheduledTasks.get(dragState.activeTask.id)?.effectiveDays ?? 10}
                  />
                  <BoardV3DragInfoOverlay
                    task={dragState.activeTask}
                    targetColumn={dragState.snapTarget.column}
                    targetDay={dragState.snapTarget.day}
                    scheduledTasks={scheduledTasks}
                  />
                </>
              )}

              {/* Box selection overlay */}
              {boxSelect.selectionRect && (
                <BoardV3SelectionOverlay rect={boxSelect.selectionRect} />
              )}
            </div>
          </div>
        </div>

        <DragOverlay>
          {dragState.activeTask && (
            <BoardV3TaskBoxOverlay
              task={dragState.activeTask}
              bulkDragCount={dragState.bulkDragCount}
            />
          )}
        </DragOverlay>
      </DndContext>
      <BoardV3ContextMenus tasks={tasks} />
    </TaskPoolModalProvider>
  );
});

// ============================================
// Context Menus (rendered inside TaskPoolModalProvider)
// ============================================

function BoardV3ContextMenus({
  tasks,
}: {
  tasks: import('../../types').Task[];
}) {
  const boardContextMenu = useSelector(popupStore, s => s.context.boardContextMenu);
  const columnContextMenu = useSelector(popupStore, s => s.context.columnContextMenu);
  const taskPoolRequest = useSelector(popupStore, s => s.context.taskPoolRequest);
  const openTaskPoolModal = useOpenTaskPoolModal();

  // React to taskPoolRequest from toolbar
  useEffect(() => {
    if (taskPoolRequest) {
      openTaskPoolModal(taskPoolRequest.column);
      popupStore.send({ type: 'popup.clearTaskPoolRequest' });
    }
  }, [taskPoolRequest, openTaskPoolModal]);

  return (
    <>
      {boardContextMenu && (
        <BoardContextMenu
          x={boardContextMenu.x}
          y={boardContextMenu.y}
          onAddTask={() => {
            const menu = popupStore.getSnapshot().context.boardContextMenu;
            if (menu) {
              popupStore.send({ type: 'popup.requestAddTask', column: menu.column, date: menu.date });
            }
            popupStore.send({ type: 'popup.closeBoardMenu' });
          }}
          onSelectAllInColumn={() => {
            const menu = popupStore.getSnapshot().context.boardContextMenu;
            if (menu) {
              const ids = tasks.filter(t => t.column === menu.column).map(t => t.id);
              selectionStore.send({ type: 'selection.set', taskIds: ids });
            }
            popupStore.send({ type: 'popup.closeBoardMenu' });
          }}
          onOpenTaskPool={() => {
            const menu = popupStore.getSnapshot().context.boardContextMenu;
            if (menu) {
              openTaskPoolModal(menu.column);
            }
            popupStore.send({ type: 'popup.closeBoardMenu' });
          }}
          onClose={() => popupStore.send({ type: 'popup.closeBoardMenu' })}
        />
      )}

      {columnContextMenu && (
        <ColumnContextMenu
          x={columnContextMenu.x}
          y={columnContextMenu.y}
          column={columnContextMenu.column}
          onEditColumn={() => {
            const menu = popupStore.getSnapshot().context.columnContextMenu;
            if (menu) {
              popupStore.send({ type: 'popup.requestEditColumn', column: menu.column, x: menu.x, y: menu.y });
            }
            popupStore.send({ type: 'popup.closeColumnMenu' });
          }}
          onSelectAllInColumn={() => {
            const menu = popupStore.getSnapshot().context.columnContextMenu;
            if (menu) {
              const ids = tasks.filter(t => t.column === menu.column).map(t => t.id);
              selectionStore.send({ type: 'selection.set', taskIds: ids });
            }
            popupStore.send({ type: 'popup.closeColumnMenu' });
          }}
          onOpenTaskPool={() => {
            const menu = popupStore.getSnapshot().context.columnContextMenu;
            if (menu) {
              openTaskPoolModal(menu.column);
            }
            popupStore.send({ type: 'popup.closeColumnMenu' });
          }}
          onDeleteColumn={() => {
            const menu = popupStore.getSnapshot().context.columnContextMenu;
            if (menu) {
              popupStore.send({ type: 'popup.requestDeleteColumn', column: menu.column });
            }
            popupStore.send({ type: 'popup.closeColumnMenu' });
          }}
          onClose={() => popupStore.send({ type: 'popup.closeColumnMenu' })}
        />
      )}
    </>
  );
}

export { BoardV3 as BoardV3Content };
export default BoardV3;
